import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "../../i18n";

const ToastContext = createContext(null);

const ICONS = {
  success: "check_circle",
  error: "error",
  info: "info",
  warning: "warning",
};

// Type-spezifische Farb-Sets — gleicher Akzent zieht sich durch
// Border-Strip, Icon-Bubble und Glow-Shadow damit der Toast pro
// Severity wiedererkennbar ist und nicht in einer dezenten Karte
// untergeht.
const TYPE_THEME = {
  success: {
    icon: "text-success",
    iconBg: "bg-success/15",
    accent: "before:bg-success",
    glow: "shadow-[0_8px_30px_rgba(76,195,138,0.18)]",
  },
  error: {
    icon: "text-error",
    iconBg: "bg-error/15",
    accent: "before:bg-error",
    glow: "shadow-[0_8px_30px_rgba(248,113,113,0.22)]",
  },
  info: {
    icon: "text-primary",
    iconBg: "bg-primary/15",
    accent: "before:bg-primary",
    glow: "shadow-[0_8px_30px_rgba(127,178,255,0.18)]",
  },
  warning: {
    icon: "text-warning",
    iconBg: "bg-warning/15",
    accent: "before:bg-warning",
    glow: "shadow-[0_8px_30px_rgba(229,165,75,0.18)]",
  },
};

let toastId = 0;

// Backward-kompatibler Adapter: alte Aufrufe nutzten `toast.success(msg, 6000)`,
// neue können `toast.success(msg, { duration: 6000, action: {…} })`.
function emit(type, msg, opts, addToast) {
  if (opts == null || typeof opts === "number") {
    return addToast(msg, type, opts);
  }
  return addToast(msg, type, opts.duration, opts.action ?? null);
}

function ToastItem({ toast, onDismiss }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const theme = TYPE_THEME[toast.type] || TYPE_THEME.info;
  const dismissLabel = t("layout.toast.dismissAria");

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss(toast.id), 300);
    }, toast.duration);
    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  const dismissNow = () => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  return (
    <div
      role="alert"
      className={`relative flex items-center gap-3 pl-5 pr-4 py-4 rounded-[10px] glass-card-elevated overflow-hidden
        before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 ${theme.accent}
        ${theme.glow}
        transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none
        ${visible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-4 scale-95"}`}
    >
      <span
        className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${theme.iconBg}`}
      >
        <span className={`material-symbols-outlined text-[22px] ${theme.icon}`}>
          {ICONS[toast.type]}
        </span>
      </span>
      <p className="text-sm font-semibold text-on-surface flex-1 leading-snug">{toast.message}</p>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            try {
              toast.action.onClick?.();
            } finally {
              dismissNow();
            }
          }}
          className="shrink-0 px-3.5 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-bold hover:opacity-90 transition-opacity"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={dismissNow}
        className="shrink-0 text-on-surface-variant/60 hover:text-on-surface p-1 rounded-full hover:bg-surface-container-high transition-colors"
        aria-label={dismissLabel}
      >
        <span className="material-symbols-outlined text-base">close</span>
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((message, type = "info", duration = 4000, action = null) => {
    const id = ++toastId;
    setToasts([{ id, message, type, duration, action }]);
  }, []);

  // Type-Helper akzeptieren entweder (msg, duration?) oder
  // (msg, options) mit { duration, action: { label, onClick } } —
  // bestehende Aufrufe bleiben kompatibel weil duration als Number
  // den ersten Branch trifft.
  const toast = useMemo(
    () => ({
      success: (msg, opts) => emit("success", msg, opts, addToast),
      error: (msg, opts) => emit("error", msg, opts, addToast),
      info: (msg, opts) => emit("info", msg, opts, addToast),
      warning: (msg, opts) => emit("warning", msg, opts, addToast),
    }),
    [addToast],
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Oben mittig statt unten rechts (Max, 04.08.): kurz reinsliden,
          nichts verdecken, wieder verschwinden. */}
      <div className="fixed top-4 left-1/2 z-[9999] flex w-[420px] max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col gap-2">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
